import { gaClientIdRequestBody, syncAnalyticsIdentity, trackLogin } from './analytics.js';
import { clientCookieOptions, GOOGLE_CLIENT_ID } from './authConstants.js';

export const DISPLAY_COOKIE_NAMES = ['given_name', 'picture', 'email'];

function applyAnalyticsIdentity(user) {
    if (!user) {
        return;
    }
    syncAnalyticsIdentity({
        gaClientId: user.gaClientId,
        gaUserId: user.gaUserId,
    });
}

async function authFetch(path, options = {}) {
    const res = await fetch(`/api/auth${path}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...options.headers },
        ...options,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data.error || `Request failed (${res.status})`);
    }
    return { res, data };
}

/**
 * Renders Google Sign-In button in the specified element
 * @param {string} elementId - ID of the element where the button should be rendered
 * @param {Function} onSuccessCallback - Callback function to call after successful login
 */
export function renderGoogleLoginButton(elementId, onSuccessCallback, { text = 'sign_in' } = {}) {
    try {
        window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: onSuccessCallback,
        });

        window.google.accounts.id.renderButton(document.getElementById(elementId), {
            theme: "outline",
            size: "large",
            text,
            width: 280,
        });

        window.google.accounts.id.prompt();
    } catch (e) {
        console.log("Error", e);
    }
}

/**
 * Sets client-readable profile cookies (session is HttpOnly on the server).
 * @param {Function} setCookie
 * @param {object} user
 */
export function setDisplayProfileCookies(setCookie, user) {
    if (!user) return;
    setCookie('given_name', user.given_name || '', { path: '/' });
    setCookie('email', user.email || '', { path: '/' });
    setCookie('picture', user.picture || '', { path: '/' });
}

export function clearDisplayProfileCookies(removeCookie) {
    const opts = clientCookieOptions();
    for (const name of DISPLAY_COOKIE_NAMES) {
        removeCookie(name, opts);
    }
}

/**
 * @returns {Promise<object|null>} public user profile or null if not authenticated
 */
export async function fetchAuthMe() {
    const res = await fetch('/api/auth/me', {
        method: 'GET',
        credentials: 'include',
    });
    if (res.status === 401) {
        return null;
    }
    if (!res.ok) {
        throw new Error(`auth/me failed: ${res.status}`);
    }
    return res.json();
}

/**
 * Exchanges Google ID token for server session. Caller should persist profile via AuthContext.
 * @param {Object} response - Response from Google Sign-In
 * @param {Function} [onAfterLogin] - Called with public user profile after session is created
 */
export async function handleGoogleLoginSuccess(response, onAfterLogin, { authEvent = 'login' } = {}) {
    const { data: user } = await authFetch('/google', {
        method: 'POST',
        body: JSON.stringify({
            idToken: response.credential,
            ...gaClientIdRequestBody(),
        }),
    });
    // sign_up is sent server-side via Measurement Protocol on new account create.
    if (authEvent !== 'sign_up') {
        trackLogin('google');
    }
    applyAnalyticsIdentity(user);
    if (onAfterLogin) {
        onAfterLogin(user);
    }
    return user;
}

export async function registerWithEmail({ email, password, name }) {
    const { data } = await authFetch('/register', {
        method: 'POST',
        body: JSON.stringify({
            email,
            password,
            name,
            ...gaClientIdRequestBody(),
        }),
    });
    return data;
}

export async function verifyEmailToken(token) {
    const { data } = await authFetch('/verify-email', {
        method: 'POST',
        body: JSON.stringify({ token }),
    });
    return data;
}

export async function loginWithEmail({ email, password }, onAfterLogin) {
    const { data: user } = await authFetch('/login', {
        method: 'POST',
        body: JSON.stringify({
            email,
            password,
            ...gaClientIdRequestBody(),
        }),
    });
    trackLogin('email');
    applyAnalyticsIdentity(user);
    if (onAfterLogin) {
        onAfterLogin(user);
    }
    return user;
}

export async function requestPasswordReset(email) {
    const { data } = await authFetch('/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
    });
    return data;
}

export async function resetPasswordWithToken({ token, password }) {
    const { data } = await authFetch('/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
    });
    return data;
}

/**
 * Logs out: revokes server session and clears profile cookies.
 * @param {Function} removeCookie
 * @param {Function} [renderLoginButton]
 */
export async function logout(removeCookie, renderLoginButton) {
    try {
        await fetch('/api/auth/logout', {
            method: 'POST',
            credentials: 'include',
        });
    } catch (e) {
        console.log('Logout request failed:', e);
    }
    clearDisplayProfileCookies(removeCookie);

    if (renderLoginButton) {
        setTimeout(renderLoginButton);
    }
}

export { fetchAuthMe as fetchMe, handleGoogleLoginSuccess as exchangeGoogleToken };
